import { useState, useCallback, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { useAdjustKoins } from "../hooks/useUsers";
import { useLocale } from "../../../context/LanguageContext";

interface AdjustKoinsDialogProps {
  userId: string;
  userName: string;
  currentBalance: number;
  onClose: () => void;
}

export function AdjustKoinsDialog({
  userId,
  userName,
  currentBalance,
  onClose,
}: AdjustKoinsDialogProps) {
  const { t } = useLocale();
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [adjustKoins, { loading }] = useAdjustKoins();

  // 2026-05-18: referenceId is required server-side to prevent silent
  // deduplication of legitimate repeat adjustments. We mint a UUID on
  // first confirm and reuse it on retry-after-error so the backend
  // dedupes a partially-applied attempt instead of double-crediting.
  const pendingReferenceId = useRef<string | null>(null);

  const parsedAmount = parseInt(amount, 10);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount !== 0;
  const isValidReason = reason.trim().length >= 10;
  const isValid = isValidAmount && isValidReason;

  const projectedBalance = isValidAmount
    ? currentBalance + parsedAmount
    : currentBalance;

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    setError(null);

    if (!pendingReferenceId.current) {
      pendingReferenceId.current = crypto.randomUUID();
    }
    const referenceId = pendingReferenceId.current;

    try {
      await adjustKoins({
        variables: {
          input: {
            userId,
            amount: parsedAmount,
            reason: reason.trim(),
            referenceId,
          },
        },
      });
      // Successful submit — mint a fresh referenceId for any subsequent
      // adjustment in this session.
      pendingReferenceId.current = null;
      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t("users.adjust.error");
      setError(message);
      // Keep referenceId so a retry of the same operation dedupes server-side.
    }
  }, [adjustKoins, userId, parsedAmount, reason, isValid, onClose, t]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t("users.adjust.title")}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {userName} · {t("users.adjust.currentLabel")}{" "}
            {currentBalance.toLocaleString()} Koins
          </p>
        </div>

        {/* Warning Banner */}
        <div className="mx-5 mt-4 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2.5 dark:border-yellow-800 dark:bg-yellow-900/20">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
          <p className="text-xs text-yellow-700 dark:text-yellow-400">
            {t("users.adjust.warning")}
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4 px-5 py-4">
          {/* Amount */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("users.adjust.amountLabel")}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t("users.adjust.amountPlaceholder")}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {isValidAmount && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("users.adjust.newBalance")}{" "}
                <span
                  className={
                    projectedBalance < 0
                      ? "font-medium text-destructive"
                      : "font-medium text-foreground"
                  }
                >
                  {projectedBalance.toLocaleString()} Koins
                </span>
              </p>
            )}
            {isValidAmount && projectedBalance < 0 && (
              <p className="mt-0.5 text-xs text-destructive">
                {t("users.adjust.negativeWarning")}
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("users.adjust.reasonLabel")}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("users.adjust.reasonPlaceholder")}
              rows={3}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="mt-0.5 text-right text-xs text-muted-foreground">
              {reason.trim().length}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-9 rounded-md border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || loading}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? t("users.adjust.submitting") : t("users.adjust.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
